import React, { useState } from 'react';
import { useLists } from '../../contexts/ListContext';
import { Button, Menu, MenuItem, ListItemIcon, Checkbox, Typography, Box } from '@mui/material';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import CheckIcon from '@mui/icons-material/Check';

interface AddToListButtonProps {
  productId: number;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onAddToList?: () => void;
}

const AddToListButton: React.FC<AddToListButtonProps> = ({ 
  productId, 
  anchorEl, 
  onClose,
  onAddToList
}) => {
  const { lists, addToList, removeFromList, createList } = useLists();
  const [newListName, setNewListName] = useState('');
  const [showNewListInput, setShowNewListInput] = useState(false);
  const [internalAnchorEl, setInternalAnchorEl] = useState<HTMLElement | null>(null);

  // Use external anchorEl if provided, otherwise use internal state
  const currentAnchorEl = anchorEl !== undefined ? anchorEl : internalAnchorEl;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (anchorEl === undefined) {
      setInternalAnchorEl(event.currentTarget);
    }
  };

  const handleMenuClose = () => {
    if (anchorEl === undefined) {
      setInternalAnchorEl(null);
    } else {
      onClose?.();
    }
    setShowNewListInput(false);
    setNewListName('');
  };

  const handleAddToNewList = async () => {
    if (!newListName.trim()) return;
    
    try {
      const newList = await createList(newListName);
      await addToList(newList.id, productId);
      setNewListName('');
      setShowNewListInput(false);
      onAddToList?.();
    } catch (error) {
      console.error('Failed to create list or add product:', error);
    }
  };

  const handleToggleInList = async (listId: string, isInList: boolean) => {
    try {
      if (isInList) {
        await removeFromList(listId, productId);
      } else {
        await addToList(listId, productId);
        onAddToList?.();
      }
    } catch (error) {
      console.error('Failed to update list:', error);
    }
  };

  return (
    <>
      <Button 
        size="small" 
        startIcon={<PlaylistAddIcon />}
        onClick={handleMenuOpen}
        aria-label="Add to list"
      >
        Add to List
      </Button>
      
      <Menu
        anchorEl={currentAnchorEl}
        open={Boolean(currentAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        PaperProps={{
          style: {
            width: 250,
            maxHeight: 300,
          },
        }}
      >
        {!showNewListInput ? (
          <MenuItem onClick={() => setShowNewListInput(true)}>
            <ListItemIcon>
              <PlaylistAddIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="inherit">Create new list</Typography>
          </MenuItem>
        ) : (
          <Box p={1}>
            <Box display="flex" alignItems="center" mb={1}>
              <input
                type="text"
                value={newListName}
                onChange={(e) => setNewListName(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddToNewList()}
                placeholder="New list name"
                style={{
                  flex: 1,
                  padding: '8px',
                  border: '1px solid #ccc',
                  borderRadius: '4px',
                  marginRight: '8px',
                }}
                autoFocus
              />
              <Button 
                size="small" 
                onClick={handleAddToNewList}
                disabled={!newListName.trim()}
              >
                Create
              </Button>
            </Box>
          </Box>
        )}
        
        <Box px={2} py={1}>
          <Typography variant="overline" color="textSecondary">My Lists</Typography>
        </Box>
        
        {lists.length === 0 && !showNewListInput && (
          <Box px={2} py={1}>
            <Typography variant="body2" color="textSecondary">
              No lists yet. Create one to get started.
            </Typography>
          </Box>
        )}
        
        {lists.map((list) => {
          const isInList = list.productIds.includes(productId);
          return (
            <MenuItem 
              key={list.id} 
              onClick={() => handleToggleInList(list.id, isInList)}
              dense
            >
              <ListItemIcon>
                <Checkbox 
                  checked={isInList}
                  checkedIcon={<CheckIcon />}
                  size="small"
                  disableRipple
                />
              </ListItemIcon>
              <Typography variant="inherit" noWrap>
                {list.name} ({list.productIds.length})
              </Typography>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

export default AddToListButton;
